import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import RevenueGMManagement from './pages/RevenueGMManagement';
import BusinessLinesManagement from './pages/BusinessLinesManagement';
import SalesTeamManagement from './pages/SalesTeamManagement';
import MeetingsManagement from './pages/MeetingsManagement';
import OffersManagement from './pages/OffersManagement';
import TasksManagement from './pages/TasksManagement';
import UsersRolesManagement from './pages/UsersRolesManagement';
import ProfileSettings from './pages/ProfileSettings';
import ChatSettings from './pages/ChatSettings';

const { FiDollarSign, FiLayers, FiUsers, FiCalendar, FiGift, FiCheckSquare, FiUserCheck, FiSettings, FiMessageSquare, FiChevronRight } = FiIcons;

const DataManagement = () => {
  const location = useLocation();

  const navigation = [
    { name: 'Revenue & GM', href: '/data-management/revenue-gm', icon: FiDollarSign, color: 'green' },
    { name: 'Business Lines', href: '/data-management/business-lines', icon: FiLayers, color: 'indigo' },
    { name: 'Sales Team', href: '/data-management/sales-team', icon: FiUsers, color: 'blue' },
    { name: 'Meetings', href: '/data-management/meetings', icon: FiCalendar, color: 'purple' },
    { name: 'Offers', href: '/data-management/offers', icon: FiGift, color: 'orange' },
    { name: 'Tasks', href: '/data-management/tasks', icon: FiCheckSquare, color: 'teal' },
    { name: 'Users & Roles', href: '/data-management/users-roles', icon: FiUserCheck, color: 'pink' },
    { name: 'Chat Settings', href: '/data-management/chat-settings', icon: FiMessageSquare, color: 'cyan' },
    { name: 'Profile Settings', href: '/data-management/profile', icon: FiSettings, color: 'gray' },
  ];

  const isActive = (href) => location.pathname === href;

  const getCurrentPageName = () => {
    const currentNav = navigation.find(item => isActive(item.href));
    return currentNav?.name || 'Data Management';
  };

  const getColorClass = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
      purple: 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200',
      teal: 'bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200',
      pink: 'bg-pink-100 text-pink-700 border-pink-200 hover:bg-pink-200',
      cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200 hover:bg-cyan-200',
      gray: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Data Management</h1>
            <p className="text-gray-600">Manage your business data, teams, and settings</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Current Section</p>
            <p className="text-lg font-semibold text-gray-900">{getCurrentPageName()}</p>
          </div>
        </div>
      </motion.div>

      {/* Navigation Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
          {navigation.map((item, index) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={item.href}
                className={`flex flex-col items-center p-3 rounded-lg border transition-all hover:scale-105 ${
                  isActive(item.href)
                    ? `${getColorClass(item.color)} shadow-md transform scale-105`
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={item.icon} className="w-5 h-5 mb-2" />
                <span className="text-xs font-medium text-center leading-tight">{item.name}</span>
                {isActive(item.href) && (
                  <SafeIcon icon={FiChevronRight} className="w-3 h-3 mt-1 opacity-70" />
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Routes>
          <Route path="/" element={<RevenueGMManagement />} />
          <Route path="/revenue-gm" element={<RevenueGMManagement />} />
          <Route path="/business-lines" element={<BusinessLinesManagement />} />
          <Route path="/sales-team" element={<SalesTeamManagement />} />
          <Route path="/meetings" element={<MeetingsManagement />} />
          <Route path="/offers" element={<OffersManagement />} />
          <Route path="/tasks" element={<TasksManagement />} />
          <Route path="/users-roles" element={<UsersRolesManagement />} />
          <Route path="/chat-settings" element={<ChatSettings />} />
          <Route path="/profile" element={<ProfileSettings />} />
        </Routes>
      </motion.div>
    </div>
  );
};

export default DataManagement;